import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  CheckCircle, XCircle, Loader2, Play, UserPlus, Database,
  Mail, Key, Shield, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SignupFlowTestsSection = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runTests = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/health/tests/signup-flow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setResults(data);
      
      if (data.summary?.passRate === 100) {
        toast.success(`Signup Flow: All ${data.summary.total} tests passed!`);
      } else {
        toast.warning(`Signup Flow: ${data.summary.passed}/${data.summary.total} tests passed`);
      }
    } catch (error) {
      console.error('Error running signup flow tests:', error);
      toast.error('Failed to run signup flow tests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'skipped':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default:
        return <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />;
    }
  };

  const getTestIcon = (testName) => {
    if (testName.includes('Email')) return <Mail className="w-4 h-4" />;
    if (testName.includes('Slug')) return <Key className="w-4 h-4" />;
    if (testName.includes('PostgreSQL')) return <Database className="w-4 h-4" />;
    if (testName.includes('Duplicate')) return <Shield className="w-4 h-4" />;
    return <UserPlus className="w-4 h-4" />;
  };

  return (
    <Card className="border-purple-200 bg-purple-50/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-purple-600" />
            <CardTitle className="text-lg text-purple-900">
              Signup Flow (PostgreSQL E2E)
            </CardTitle>
          </div>
          <Button
            onClick={runTests}
            disabled={loading}
            size="sm"
            className="bg-purple-600 hover:bg-purple-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Run Tests
              </>
            )}
          </Button>
        </div>
        <p className="text-sm text-purple-700 mt-1">
          Tests the complete signup flow: slug check → email check → account creation → PostgreSQL verification
        </p>
      </CardHeader>
      
      <CardContent>
        {results ? (
          <div className="space-y-4">
            {/* Summary */}
            <div className={`p-3 rounded-lg ${
              results.summary?.passRate === 100 
                ? 'bg-green-100 border border-green-300' 
                : 'bg-yellow-100 border border-yellow-300'
            }`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">
                  {results.summary?.passed}/{results.summary?.total} tests passed
                </span>
                <span className={`text-lg font-bold ${
                  results.summary?.passRate === 100 ? 'text-green-700' : 'text-yellow-700'
                }`}>
                  {results.summary?.passRate}%
                </span>
              </div>
            </div>

            {/* Individual Tests */}
            <div className="space-y-2">
              {results.tests?.map((test, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border ${
                    test.status === 'passed' 
                      ? 'bg-green-50 border-green-200' 
                      : test.status === 'failed'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {getStatusIcon(test.status)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {getTestIcon(test.name)}
                        <span className="font-medium text-sm">{test.name}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{test.message}</p>
                      {test.endpoint && (
                        <p className="text-xs text-gray-500 mt-1 font-mono">
                          {test.endpoint}
                        </p>
                      )}
                      {test.database && (
                        <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                          <Database className="w-3 h-3" />
                          {test.database}
                        </span>
                      )}
                      {test.details && (
                        <div className="mt-2 p-2 bg-white rounded text-xs font-mono">
                          <pre className="whitespace-pre-wrap">
                            {JSON.stringify(test.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Cleanup Status */}
            {results.cleanup && (
              <div className={`p-2 rounded text-xs ${
                results.cleanup.status === 'success' 
                  ? 'bg-gray-100 text-gray-600' 
                  : 'bg-red-100 text-red-600'
              }`}>
                Cleanup: {results.cleanup.message}
              </div>
            )}

            {/* Timestamp */}
            <p className="text-xs text-gray-500 text-right">
              Last run: {new Date(results.timestamp).toLocaleString()}
            </p>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <UserPlus className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Click "Run Tests" to verify the signup flow</p>
            <p className="text-xs mt-1">Tests: Slug check, Email check, Account creation, PostgreSQL verification</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SignupFlowTestsSection;
